import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

export default function CoachMTMWeek() {
  const { weekNumber } = useParams();
  const n = parseInt(weekNumber, 10);
  const [week, setWeek] = useState(null);
  const [supplements, setSupplements] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/mtm/weeks/${n}`),
      api.get("/supplements"),
    ]).then(([w, s]) => { setWeek(w.data); setSupplements(s.data); }).catch(() => {});
  }, [n]);

  const set = (k) => (e) => setWeek({ ...week, [k]: e.target.value });
  const toggleSupp = (id) => {
    const cur = week.supplement_ids || [];
    setWeek({ ...week, supplement_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const updatePrompts = (text) => setWeek({ ...week, habit_prompts: text.split("\n").map((s) => s.trim()).filter(Boolean) });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: week.title, summary: week.summary, learning: week.learning,
        zoom_url: week.zoom_url, zoom_when: week.zoom_when,
        nutrition_notes: week.nutrition_notes, workout_notes: week.workout_notes,
        supplement_ids: week.supplement_ids || [],
        habit_prompts: week.habit_prompts || [],
        preview_next: week.preview_next,
      };
      const { data } = await api.patch(`/mtm/weeks/${n}`, payload);
      setWeek(data); toast.success("Saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!week) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="coach-week-editor">
      <Link to="/coach/mtm" className="btn-ghost"><ArrowLeft size={12}/> Back to MTM</Link>

      <header>
        <p className="overline">{week.phase} phase · Editing</p>
        <h1 className="font-display text-4xl mt-6">Week {week.week}</h1>
        <div className="bronze-rule mt-5" />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-luxe space-y-5">
          <div><label className="label-luxe">Title</label>
            <input className="input-luxe" value={week.title || ""} onChange={set("title")} data-testid="week-title" /></div>
          <div><label className="label-luxe">One-line summary</label>
            <input className="input-luxe" value={week.summary || ""} onChange={set("summary")} data-testid="week-summary" /></div>
          <div><label className="label-luxe">Zoom URL</label>
            <input className="input-luxe" value={week.zoom_url || ""} onChange={set("zoom_url")} placeholder="https://us02web.zoom.us/j/…" data-testid="week-zoom-url" /></div>
          <div><label className="label-luxe">Zoom when (human readable)</label>
            <input className="input-luxe" value={week.zoom_when || ""} onChange={set("zoom_when")} placeholder="Monday · 7pm CT" /></div>
        </div>
        <div className="card-luxe space-y-5">
          <div><label className="label-luxe">The teaching (rich content)</label>
            <textarea rows={10} className="input-luxe" value={week.learning || ""} onChange={set("learning")} placeholder="The lesson, the science, the protocol…" data-testid="week-learning" /></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-luxe space-y-3">
          <label className="label-luxe">Nutrition notes</label>
          <textarea rows={6} className="input-luxe" value={week.nutrition_notes || ""} onChange={set("nutrition_notes")} data-testid="week-nutrition" />
        </div>
        <div className="card-luxe space-y-3">
          <label className="label-luxe">Workout / movement notes</label>
          <textarea rows={6} className="input-luxe" value={week.workout_notes || ""} onChange={set("workout_notes")} data-testid="week-workout" />
        </div>
      </div>

      <div className="card-luxe">
        <label className="label-luxe">Weekly rituals (one per line)</label>
        <textarea rows={5} className="input-luxe"
          value={(week.habit_prompts || []).join("\n")}
          onChange={(e) => updatePrompts(e.target.value)}
          placeholder="Drink half your body weight in ounces of water&#10;30 min Zone 2 walk × 4&#10;Magnesium glycinate at bedtime"
          data-testid="week-habits" />
      </div>

      <div className="card-luxe">
        <label className="label-luxe">Supplements & peptides for this week</label>
        <p className="font-soft text-sm mt-2 mb-4 italic" style={{ color: "var(--text-tertiary)" }}>
          Toggle any items from your affiliate library. Add new items on the MTM → Supplements tab.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {supplements.map((s) => {
            const on = (week.supplement_ids || []).includes(s.supplement_id);
            return (
              <button key={s.supplement_id} type="button" onClick={() => toggleSupp(s.supplement_id)}
                data-testid={`toggle-${s.supplement_id}`}
                className="text-left p-3 border transition-colors"
                style={{
                  borderColor: on ? "var(--accent)" : "var(--border-strong)",
                  background: on ? "rgba(168,152,128,0.08)" : "transparent",
                }}>
                <div className="font-display text-sm">{s.name}</div>
                <div className="font-label text-[9px] tracking-widest uppercase mt-1" style={{ color: "var(--text-tertiary)" }}>{s.category}{s.brand ? ` · ${s.brand}` : ""}</div>
              </button>
            );
          })}
          {supplements.length === 0 && <p className="text-secondary-luxe text-sm">No supplements yet.</p>}
        </div>
      </div>

      <div className="card-luxe">
        <label className="label-luxe">Preview for next week (what to prepare)</label>
        <textarea rows={3} className="input-luxe" value={week.preview_next || ""} onChange={set("preview_next")} data-testid="week-preview-next" />
      </div>

      <button onClick={save} disabled={busy} className="btn-primary" data-testid="week-save">
        <Save size={12}/> {busy ? "Saving…" : "Save week"}
      </button>
    </div>
  );
}
